import { Injectable, untracked } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { StateService } from './state.service';
import { TeamApiService } from '../core/services/team-api.service';

@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private cache = new Map<string, string[]>();
  private inFlight = new Map<string, Observable<string[]>>();

  constructor(private stateService: StateService, private teamApi: TeamApiService) {}

  private normalizeEmail(email: unknown): string {
    return String(email || '').toLowerCase().trim();
  }

  private normalizeId(value: unknown): string {
    return String(value ?? '').trim();
  }

  getPermissionsForCompany$(applicationId: string | number): Observable<string[]> {
    const key = this.normalizeId(applicationId);
    if (!key) return of([]);

    const cached = this.cache.get(key);
    if (cached) return of(cached);

    // Avoid accidentally registering signal dependencies when this is called inside an `effect()`.
    const user = untracked(() => this.stateService.userInfo);

    const existing = this.inFlight.get(key);
    if (existing) return existing;

    const request$ = this.teamApi.listInvitations(key).pipe(
      switchMap((invites: any[]) => {
        const email = this.normalizeEmail(user?.email);
        const acceptedInvite = (invites || []).find((inv: any) =>
          (inv?.invitee_uid ? this.normalizeId(inv.invitee_uid) === this.normalizeId(user?.uid) : Boolean(email) && this.normalizeEmail(inv?.email) === email) && String(inv?.status || '').toLowerCase() === 'accepted'
        );

        if (acceptedInvite) {
          const invitePermissions = Array.isArray(acceptedInvite.permissions) ? acceptedInvite.permissions : [];
          return of(invitePermissions.filter((p: any) => typeof p === 'string'));
        }

        return this.teamApi.listMembers(key).pipe(
          map((res: any) => {
            const members = Array.isArray(res) ? res : res?.members ?? res?.['members'] ?? [];
            const uid = this.normalizeId(user?.uid);
            const contactId = this.normalizeId(user?.contactId);

            const matches = (members || []).filter((member: any) => {
              const memberEmail = this.normalizeEmail(member?.email);
              const boundUid = this.normalizeId(member?.uid || member?.invitee_uid);
              if (boundUid) return Boolean(uid) && boundUid === uid;
              const memberUid = this.normalizeId(member?.contactId ?? member?.altomateId);
              if (email && memberEmail && memberEmail === email) return true;
              if (uid && memberUid && memberUid === uid) return true;
              if (contactId && memberUid && memberUid === contactId) return true;
              return false;
            });

            const found = matches.sort((a: any, b: any) => {
              const aInvitation = String(a?.source || '').toLowerCase() === 'invitation' ? 1 : 0;
              const bInvitation = String(b?.source || '').toLowerCase() === 'invitation' ? 1 : 0;
              return bInvitation - aInvitation;
            })[0];

            const permissions = Array.isArray(found?.permissions) ? found.permissions : [];
            return permissions.filter((p: any) => typeof p === 'string');
          }),
        );
      }),
      catchError(() => of([])),
      tap((permissions) => this.cache.set(key, permissions)),
      finalize(() => this.inFlight.delete(key)),
      shareReplay(1),
    );

    this.inFlight.set(key, request$);
    return request$;
  }

  clearCache(companyId?: string | number): void {
    if (companyId == null) {
      this.cache.clear();
      return;
    }
    this.cache.delete(this.normalizeId(companyId));
  }
}
